import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { IDataChart } from './IDataChart';
import {
  adjustChartToLegendHeight,
  calculateTimeIntervalByDay,
  centerLegendItems,
  getDateLabelByPeriod,
} from '@/creditflowresearch/ui/common/components/FinsightChart/helpers';
import { Periods } from '@/creditflowresearch/ui/common/constants';
import { ChartsGrouping, ChartType } from '@/creditflowresearch/application/Bonds/charts/constants';

import styles from './finsightChart.module.scss';

const defaultChartHeight = 380;
const allowedLegendHeight = 200;

const timeIntervals = {
  [Periods.MONTH]: calculateTimeIntervalByDay(30),
  [Periods.QUARTER]: calculateTimeIntervalByDay(90),
  [Periods.YEAR]: calculateTimeIntervalByDay(365),
};

interface IOptions {
  tooltipComponent: React.ElementType,
  period: Periods,
  chartType: ChartType,
  grouping: ChartsGrouping,
  events?: {
    load?: () => void,
    redraw?: () => void,
  },
}

export const getChartConfig = <T extends IDataChart>(
  collection: T[],
  { tooltipComponent, period, chartType, grouping, events }: IOptions,
) => {
  return {
    time: {
      useUTC: false,
    },
    rangeSelector: false,
    credits: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    title: {
      text: '',
    },
    chart: getBaseChartConfig(events),
    responsive: getResponsiveConfig(),
    series: collection,
    legend: getLegendConfig(),
    xAxis: getXAxisConfig(period),
    yAxis: getYAxisConfig(grouping, chartType),
    plotOptions: getPlotConfig(chartType, period),
    navigator: getNavigatorConfig(chartType, period),
    tooltip: getTooltipConfig(tooltipComponent, period, chartType),
  };
};

const getResponsiveConfig = () => ({
  rules: [{
    condition: {
      maxWidth: 300,
    },
    chartOptions: {
      chart: {
        spacingRight: 0,
      },
      legend: {
        padding: 20,
        margin: -30,
        align: 'center',
      },
      navigator: {
        enabled: false,
      },
      yAxis: {
        labels: {
          enabled: false,
        },
      },
    },
  }],
});

const getBaseChartConfig = (events) => ({
  animation: false,
  type: 'column',
  backgroundColor: '#fff',
  spacing: [20, 4, 0, 0],
  height: 380,
  panning: {
    enabled: true,
    type: 'x',
  },
  zooming: {
    mouseWheel: {
      enabled: false,
    },
  },
  events: {
    load() {
      if (typeof events.load === 'function') {
        events.load.call(this);
      }
    },
    redraw() {
      if (typeof events.redraw === 'function') {
        events.redraw.call(this);
      }
    },
    render() {
      centerLegendItems(this);
      adjustChartToLegendHeight(this, defaultChartHeight, allowedLegendHeight);
    },
  },
});

const getLegendConfig = () => ({
  borderWidth: 0,
  enabled: true,
  padding: 20,
  width: '100%',
  shadow: false,
  symbolRadius: 0,
  symbolWidth: 10,
  symbolHeight: 10,
  itemStyle: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '11px',
    lineHeight: '13px',
  },
  itemMarginBottom: 0,
  itemMarginTop: 5,
  itemDistance: 20,
  alignColumns: false,
  navigation: {
    enabled: false,
  },
});

const getXAxisConfig = (period: Periods) => ({
  type: 'datetime',
  lineColor: '#c8c8c8',
  startOnTick: false,
  endOnTick: false,
  minRange: timeIntervals[period],
  minTickInterval: timeIntervals[period],
  labels: {
    style: {
      color: '#616161',
      fontSize: '10px',
    },
    formatter() {
      return getDateLabelByPeriod(this.value, period);
    },
  },
});

const getYAxisConfig = (grouping: ChartsGrouping, chartType: ChartType) => ({
  endOnTick: true,
  showLastLabel: true,
  gridLineWidth: 1,
  gridLineColor: '#f3f3f3',
  lineColor: '#c0c0c0',
  lineWidth: 1,
  tickAmount: 5,
  labels: {
    enabled: true,
    style: {
      color: '#616161',
      fontSize: '10px',
      width: '175px',
    },
    formatter() {
      if (chartType === ChartType.STACKED) {
        return `${ this.value }%`;
      }

      if (grouping === ChartsGrouping.VOLUME) {
        return `$${ this.value / (10 ** 9) }b`;
      }

      return this.value;
    },
  },
  opposite: false,
  title: {
    text: '',
  },
});

const getPlotConfig = (chartType: ChartType, period: Periods) => ({
  bar: {
    dataLabels: {
      enabled: true,
    },
  },
  column: {
    pointPlacement: null,
    minPointLength: 5,
    borderRadius: 0,
  },
  series: {
    showInLegend: true,
    showInNavigator: true,
    type: 'column',
    color: '#ccc',
    animation: false,
    connectNulls: true,
    stickyTracking: false,
    borderWidth: 0,
    marker: {
      enabled: false,
      radius: 3,
    },
    dataGrouping: getDataGroupingConfig(period),
    stacking: chartType === ChartType.STACKED ? 'percent' : undefined,
    events: {
      legendItemClick () {
        return false;
      },
    },
  },
});

const getDataGroupingConfig = (period: Periods) => ({
  enabled: true,
  forced: true,
  units: [[
    period === Periods.YEAR ? 'year' : 'month',
    [period === Periods.QUARTER ? 3 : 1],
  ]],
});

const getNavigatorConfig = (chartType: ChartType, period: Periods) => ({
  outlineWidth: 0,
  annotationsOptions: {
    visible: true,
  },
  startOnTick: true,
  alignTicks: true,
  endOnTick: false,
  height: 25,
  margin: 20,
  maskFill: 'rgba(74, 119, 181, 0.2)',
  handles: {
    backgroundColor: '#4a76b6',
    height: 11,
    width: 9,
    lineWidth: 0,
    symbols: ['customHandler', 'customHandler'],
  },
  series: {
    showInLegend: false,
    type: 'column',
    color: '#ccc',
    dataGrouping: getDataGroupingConfig(period),
    stacking: chartType === ChartType.STACKED ? 'percent' : undefined,
  },
  xAxis: getNavigatorXAxisConfig(),
});

const getNavigatorXAxisConfig = () => ({
  type: 'datetime',
  lineColor: '#c8c8c8',
  gridLineWidth: 1,
  labels: {
    enabled: false,
  },
});

const getTooltipConfig = (TooltipComponent: React.ElementType, period: Periods, chartType: ChartType) => ({
  outside: true,
  split: false,
  enabled: true,
  shared: true,
  useHTML: true,
  followTouchMove: false,
  valueDecimals: 2,
  padding: 0,
  backgroundColor: '#fff',
  borderWidth: 0,
  className: styles.tooltipContainer,
  formatter() {
    return ReactDOMServer.renderToString(
      <TooltipComponent
        points={ this.points }
        period={ period }
        withPercent={ chartType === ChartType.STACKED }
      />,
    );
  },
});
